import { GroupShared, ProjectShared } from "pages/OrganizationAdmin/types";
import React, { useEffect, useMemo, useState, memo } from "react";
import { Tables, Typography, showToast } from "@deeptrue-frontend/ui";
import Search from "components/Fields/Search";
import api from "api";
import { ProjectType } from "pages/OrganizationAdmin/types";
import Project from "./Project";
import { useFormatMessage } from "components/IntlProvider";

const { Table, TableBody, TableHeader } = Tables;

interface Props {
  groupData: GroupShared;
  isLoaded: boolean;
  organizationCodename: string;
  refreshGroup: () => void;
}

const Projects = memo(
  ({ groupData, organizationCodename, isLoaded, refreshGroup }: Props) => {
    const [searchedProjects, setSearchedProjects] = useState<ProjectType[]>([]);
    const [groupProjects, setGroupProjects] = useState<ProjectShared[]>([]);
    const t = useFormatMessage();

    const getProjects = (searchValue?: string) => {
      return api.resources.organization.projects
        .getProjects({
          organizationCodename: organizationCodename,
          params: {
            search: searchValue,
          },
        })
        .then(({ data }) => {
          setSearchedProjects(data.results);
        });
    };

    useEffect(() => {
      getProjects();
      // eslint-disable-next-line
    }, []);

    useEffect(() => {
      setGroupProjects(groupData.shared_projects);
    }, [groupData.shared_projects]);

    const addProjectToGroup = (id: number) => {
      const projects = [
        ...groupData.shared_projects.map((project) => project.id),
        id,
      ];

      return api.resources.organization.groups
        .updateGroup({
          organizationCodename: organizationCodename,
          groupId: groupData.id as number,
          data: { shared_projects: projects },
        })
        .then(() => {
          refreshGroup();
          showToast({
            type: "success",
            title: t("organization.addedTo", "{x} added to {y}", {
              x: t("organization.project", "Project"),
              y: t("organization.group", "group"),
            }),
          });
        });
    };

    const removeProjectFromGroup = (id: number) => {
      const projects = groupData.shared_projects
        .map((project) => project.id)
        .filter((projectId) => projectId !== id);

      return api.resources.organization.groups
        .updateGroup({
          organizationCodename: organizationCodename,
          groupId: groupData.id as number,
          data: {
            shared_projects: projects,
          },
        })
        .then(() => {
          refreshGroup();
          showToast({
            type: "success",
            title: t("organization.removedFrom", "{x} removed from {y}", {
              x: t("organization.project", "Project"),
              y: t("organization.group", "group"),
            }),
          });
        });
    };

    const options = searchedProjects.map((project: ProjectType) => ({
      id: project.id as number,
      label: `${project.name}  (${project.product_name || "-"})`,
    }));

    const filteredOptions = useMemo(() => {
      return options.filter((searchedUser: { id: number; label: string }) => {
        const itemIndex = groupProjects.findIndex((project) => {
          return project.id === searchedUser.id;
        });

        return itemIndex === -1;
      });
    }, [groupProjects, options]);

    return (
      <>
        <Typography fontWeight={600} mb="1rem">
          {t("organization.shareWithX", "Share {x} with {y}", {
            x: t("organization.project", "Project"),
            y: t("organization.group", "Group"),
          })}
        </Typography>
        <Search
          options={filteredOptions.map(({ id, label }) => ({
            value: id,
            label,
          }))}
          styles={{
            maxWidth: "450px",
          }}
          name="user"
          onChange={(_name, project) => {
            if (project) {
              addProjectToGroup(project.value);
            }
          }}
          placeholder={t("organization.searchValue", "Search {name}", {
            name: t("organization.projects", "projects"),
          })}
          asyncSeach={(searchedValue: string) => getProjects(searchedValue)}
        />
        <Typography fontWeight={600} mb="1rem" mt="2rem">
          {t("organization.sharedWith", "{x} shared with {y}", {
            x: t("organization.projects", "Projects"),
            y: t("organization.group", "group"),
          })}
          :
        </Typography>
        <Table>
          <TableHeader
            cells={[
              {
                text: t("organization.columns.name", "Name"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
              {
                text: t("organization.columns.product", "Product"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
              {
                text: t("organization.columns.status", "Status"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
              {
                text: t("organization.columns.resources", "Resources"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
            ]}
          />

          <TableBody
            isLoading={!isLoaded}
            withLoader
            isData={groupData.shared_projects.length > 0}
            noDataText={t(
              "noresults",
              "No results. We have nothing to show here."
            )}
          >
            {groupProjects.map((project: ProjectShared) => {
              return (
                <Project
                  id={project.id}
                  name={project.name}
                  productName={project.product_name}
                  key={project.id}
                  removeProject={removeProjectFromGroup}
                  status={project.status}
                />
              );
            })}
          </TableBody>
        </Table>
      </>
    );
  }
);

export default Projects;
